import { FC } from "react";
import { Col, Row, SimpleCard, Space, Typography } from "../../../design-system";

import { DealAnalysisOutput, Evaluation } from "../../../types/ProjectTypes";
import styles from "../ProjectView.module.less";

const { Paragraph, Text } = Typography;

// ── Helpers ─────────────────────────────────────────────────────────────

type AnalysisSummary = NonNullable<DealAnalysisOutput["summary"]>;

const evaluationLabel = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return "Favorable";
        case Evaluation.NOT_FAVORABLE:
            return "Unfavorable";
        case Evaluation.NEUTRAL:
            return "Neutral";
        default:
            return "N/A";
    }
};

const subRatingLabel = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return "Good";
        case Evaluation.NOT_FAVORABLE:
            return "Weak";
        case Evaluation.NEUTRAL:
            return "Neutral";
        default:
            return "N/A";
    }
};

const subRatingClassName = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return styles.subRatingGood;
        case Evaluation.NOT_FAVORABLE:
            return styles.subRatingWeak;
        case Evaluation.NEUTRAL:
            return styles.subRatingNeutral;
        default:
            return "";
    }
};

const overallRatingClassName = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return styles.overallRatingFavorable;
        case Evaluation.NOT_FAVORABLE:
            return styles.overallRatingUnfavorable;
        case Evaluation.NEUTRAL:
            return styles.overallRatingNeutral;
        default:
            return "";
    }
};

const formatUsd = (n: number) => "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatPercent = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";

const CATEGORY_ORDER = ["Commercial", "Legal", "Operational"];

type Implication = AnalysisSummary["implications"][number];

function groupImplications(
    implications: Implication[]
): { category: string; items: Implication[] }[] {
    const map = new Map<string, Implication[]>();
    for (const imp of implications) {
        const list = map.get(imp.category) || [];
        list.push(imp);
        map.set(imp.category, list);
    }
    return CATEGORY_ORDER.filter((cat) => map.has(cat)).map((cat) => ({
        category: cat,
        items: map.get(cat)!,
    }));
}

function computeSavings(summary: AnalysisSummary, totalContractValueCents?: number) {
    let savingsDisplay: string | undefined;
    let percentDisplay: string | undefined;

    if (summary.estimatedSavings) {
        const [lowCents, highCents] = summary.estimatedSavings;
        savingsDisplay = `${formatUsd(lowCents / 100)} – ${formatUsd(highCents / 100)}`;
        if (totalContractValueCents && totalContractValueCents > 0) {
            const lowPct = (lowCents / totalContractValueCents) * 100;
            const highPct = (highCents / totalContractValueCents) * 100;
            percentDisplay = `${formatPercent(lowPct)} – ${formatPercent(highPct)} cost reduction opportunity identified`;
        }
    } else if (summary.realizedSavings != null) {
        savingsDisplay = formatUsd(summary.realizedSavings / 100);
        if (totalContractValueCents && totalContractValueCents > 0) {
            const pct = (summary.realizedSavings / totalContractValueCents) * 100;
            percentDisplay = `${formatPercent(pct)} reduction from baseline`;
        }
    }

    return { savingsDisplay, percentDisplay };
}

// ── Summary ─────────────────────────────────────────────────────────────

interface SummaryProps {
    output?: DealAnalysisOutput;
}

const evalCategories: { label: string; key: keyof NonNullable<AnalysisSummary["evaluation"]> }[] = [
    { label: "Pricing", key: "pricing" },
    { label: "Structural flexibility", key: "structuralFlexibility" },
    { label: "Operational / legal protections", key: "operationalAndLegalProtections" },
    { label: "Strategic fit", key: "strategicFit" },
];

export const Summary: FC<SummaryProps> = ({ output }) => {
    const summary = output?.summary;
    const totalContractValue = output?.dealInfo?.value;

    if (!summary) {
        return <SimpleCard header="Deal summary" headerType="primary"></SimpleCard>;
    }

    const implications = summary.implications ?? [];
    const nextActions = summary.nextActions ?? [];
    const grouped = groupImplications(implications);
    const { savingsDisplay, percentDisplay } = computeSavings(summary, totalContractValue);

    return (
        <SimpleCard header="Deal summary" headerType="primary">
            <SimpleCard type="white">
                {summary.text && (
                    <div className={styles.projectSubsection}>
                        <div className={styles.projectSubsectionHeader}>
                            What happened this round
                        </div>
                        <div className={styles.projectSubsectionBody}>
                            <Paragraph>{summary.text}</Paragraph>
                        </div>
                    </div>
                )}

                <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <div className={styles.statBox}>
                            <div className={styles.statTitle}>Economic gain</div>
                            <div className={styles.statValue} style={{ color: "#2FB801" }}>
                                {savingsDisplay ?? "N/A"}
                            </div>
                            {percentDisplay && (
                                <div className={styles.statSubtitle}>{percentDisplay}</div>
                            )}
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.statBox}>
                            <div className={styles.statTitle}>Strategic progress</div>
                            {summary.strategicProgress ? (
                                <>
                                    <div className={styles.statValue} style={{ color: "#FF8400" }}>
                                        {summary.strategicProgress.prioritiesAchieved != null
                                            ? `${summary.strategicProgress.prioritiesAchieved} of ${summary.strategicProgress.prioritiesIdentified}`
                                            : summary.strategicProgress.prioritiesIdentified}
                                    </div>
                                    <div className={styles.statSubtitle}>
                                        {summary.strategicProgress.prioritiesAchieved != null
                                            ? "Priorities achieved"
                                            : "Priorities identified"}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.statValue} style={{ color: "#FF8400" }}>
                                    N/A
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <table className={styles.implicationsTable}>
                            <thead>
                                <tr>
                                    <th colSpan={2}>Implications</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouped.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>
                                            <Text type="secondary">No implications</Text>
                                        </td>
                                    </tr>
                                ) : (
                                    grouped.flatMap((group) =>
                                        group.items.map((imp, i) => (
                                            <tr key={`${group.category}-${i}`}>
                                                {i === 0 && (
                                                    <td
                                                        className={styles.implicationCategory}
                                                        rowSpan={group.items.length}
                                                    >
                                                        {group.category}
                                                    </td>
                                                )}
                                                <td className={styles.implicationText}>
                                                    {imp.implication}
                                                </td>
                                            </tr>
                                        ))
                                    )
                                )}
                            </tbody>
                        </table>
                    </Col>
                    <Col span={12}>
                        <table className={styles.dealEvalTable}>
                            <tbody>
                                <tr className={styles.dealEvalHeader}>
                                    <td className={styles.dealEvalTitle}>Deal evaluation</td>
                                    <td
                                        className={`${styles.dealEvalOverall} ${overallRatingClassName(summary.evaluation?.overall)}`}
                                    >
                                        {evaluationLabel(summary.evaluation?.overall)}
                                    </td>
                                </tr>
                                {summary.evaluation ? (
                                    evalCategories.map(({ label, key }) => (
                                        <tr className={styles.dealEvalRow} key={key}>
                                            <td className={styles.dealEvalLabel}>{label}</td>
                                            <td>
                                                <span
                                                    className={`${styles.dealEvalBadge} ${subRatingClassName(summary.evaluation[key])}`}
                                                >
                                                    {subRatingLabel(summary.evaluation[key])}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={2}>
                                            <Text type="secondary">No evaluation data</Text>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Col>
                </Row>

                {nextActions.length > 0 && (
                    <div className={styles.projectSubsection}>
                        <div
                            className={styles.projectSubsectionHeader}
                        >{`Next actions (${nextActions.length})`}</div>
                        <Space
                            direction="vertical"
                            size={12}
                            fullWidth
                            style={{ padding: "12px 0" }}
                        >
                            {nextActions.map((item, i) => (
                                <div className={styles.nextActionCard} key={i}>
                                    <div className={styles.nextActionTitle}>{item.action}</div>
                                    <div className={styles.nextActionJustification}>
                                        {item.justification}
                                    </div>
                                </div>
                            ))}
                        </Space>
                    </div>
                )}
            </SimpleCard>
        </SimpleCard>
    );
};
